/**
 * BeezFy Premium Search System
 * Advanced search with predictive results
 */

class BeezfySearchPremium {
  constructor() {
    this.selectors = {
      drawer: '[data-beezfy-drawer="search"]',
      searchInput: '#beezfy-search-input',
      searchForm: '.beezfy-search__form',
      searchClear: '[data-search-clear]',
      searchFilters: '[data-search-filters]',
      filterButtons: '[data-filter]',
      searchLoading: '[data-search-loading]',
      searchDefault: '[data-search-default]',
      searchResults: '[data-search-results]',
      searchEmpty: '[data-search-empty]',
      searchTags: '[data-search-term]',
      trendingProducts: '[data-trending-products]'
    };

    this.config = {
      searchDelay: 300,
      minSearchLength: 2,
      maxResults: 50,
      resources: {
        type: ['product', 'article', 'page'],
        limit: 10,
        options: {
          unavailable_products: 'last',
          fields: ['title', 'handle', 'body', 'vendor', 'product_type', 'tag', 'variants.title']
        }
      }
    };

    this.state = {
      isOpen: false,
      isSearching: false,
      searchTerm: '',
      activeFilter: 'all',
      searchTimeout: null,
      abortController: null,
      cachedResults: {}
    };

    this.init();
  }

  init() {
    this.drawer = document.querySelector(this.selectors.drawer);
    if (!this.drawer) return;

    this.bindElements();
    this.bindEvents();
    this.loadTrendingProducts();
  }

  bindElements() {
    this.elements = {
      searchInput: this.drawer.querySelector(this.selectors.searchInput),
      searchForm: this.drawer.querySelector(this.selectors.searchForm),
      searchClear: this.drawer.querySelector(this.selectors.searchClear),
      searchFilters: this.drawer.querySelector(this.selectors.searchFilters),
      searchLoading: this.drawer.querySelector(this.selectors.searchLoading),
      searchDefault: this.drawer.querySelector(this.selectors.searchDefault),
      searchResults: this.drawer.querySelector(this.selectors.searchResults),
      searchEmpty: this.drawer.querySelector(this.selectors.searchEmpty),
      trendingProducts: this.drawer.querySelector(this.selectors.trendingProducts)
    };
  }

  bindEvents() {
    // Search input events
    if (this.elements.searchInput) {
      this.elements.searchInput.addEventListener('input', this.handleInput.bind(this));
      this.elements.searchInput.addEventListener('focus', this.handleFocus.bind(this));
    }

    // Form submit
    if (this.elements.searchForm) {
      this.elements.searchForm.addEventListener('submit', this.handleSubmit.bind(this));
    }

    // Clear button
    if (this.elements.searchClear) {
      this.elements.searchClear.addEventListener('click', this.handleClear.bind(this));
    }

    // Filter buttons
    const filterButtons = this.drawer.querySelectorAll(this.selectors.filterButtons);
    filterButtons.forEach(button => {
      button.addEventListener('click', this.handleFilter.bind(this));
    });

    // Popular search tags
    const searchTags = this.drawer.querySelectorAll(this.selectors.searchTags);
    searchTags.forEach(tag => {
      tag.addEventListener('click', this.handleTagClick.bind(this));
    });

    // Drawer open/close events
    document.addEventListener('drawer:open', (e) => {
      if (e.detail?.drawer === 'search') {
        this.onOpen();
      }
    });

    document.addEventListener('drawer:close', (e) => {
      if (e.detail?.drawer === 'search') {
        this.onClose();
      }
    });

    // Keyboard navigation
    this.drawer.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleInput(event) {
    const value = event.target.value.trim();

    // Show/hide clear button with fade animation
    if (this.elements.searchClear) {
      if (value) {
        this.elements.searchClear.style.display = 'flex';
        this.elements.searchClear.classList.add('show');
      } else {
        this.elements.searchClear.classList.remove('show');
        setTimeout(() => {
          if (this.elements.searchClear && !this.elements.searchClear.classList.contains('show')) {
            this.elements.searchClear.style.display = 'none';
          }
        }, 200);
      }
    }

    // Clear previous timeout
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout);
    }

    // Abort previous request
    if (this.state.abortController) {
      this.state.abortController.abort();
    }

    if (value.length >= this.config.minSearchLength) {
      // Debounce search
      this.state.searchTimeout = setTimeout(() => {
        this.performSearch(value);
      }, this.config.searchDelay);
    } else {
      this.showDefaultState();
    }
  }

  handleFocus(event) {
    if (this.state.searchTerm && this.state.searchTerm.length >= this.config.minSearchLength) {
      this.showSearchState();
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    const value = this.elements.searchInput.value.trim();

    if (value) {
      // Navigate to full search results page
      window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(value)}&type=${this.state.activeFilter === 'all' ? '' : this.state.activeFilter}`;
    }
  }

  handleClear(event) {
    this.elements.searchInput.value = '';
    this.elements.searchInput.focus();
    this.elements.searchClear.classList.remove('show');
    setTimeout(() => {
      if (this.elements.searchClear) {
        this.elements.searchClear.style.display = 'none';
      }
    }, 200);
    this.showDefaultState();
    this.state.searchTerm = '';
  }

  handleFilter(event) {
    const button = event.currentTarget;
    const filter = button.dataset.filter;

    // Update active filter
    this.drawer.querySelectorAll(this.selectors.filterButtons).forEach(btn => {
      btn.classList.toggle('active', btn === button);
    });

    this.state.activeFilter = filter;

    // Re-search with new filter if there's a search term
    if (this.state.searchTerm && this.state.searchTerm.length >= this.config.minSearchLength) {
      this.performSearch(this.state.searchTerm);
    }
  }

  handleTagClick(event) {
    const term = event.currentTarget.dataset.searchTerm;
    this.elements.searchInput.value = term;
    this.performSearch(term);
  }

  handleKeydown(event) {
    if (!this.state.isOpen) return;

    switch(event.key) {
      case 'Escape':
        this.closeDrawer();
        break;
      case 'ArrowDown':
        this.navigateResults('down');
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.navigateResults('up');
        event.preventDefault();
        break;
      case 'Enter':
        const activeResult = this.drawer.querySelector('.beezfy-search__result-item--active');
        if (activeResult) {
          const link = activeResult.querySelector('a');
          if (link) link.click();
          event.preventDefault();
        }
        break;
    }
  }

  async performSearch(searchTerm) {
    this.state.searchTerm = searchTerm;

    // Check cache
    const cacheKey = `${searchTerm}_${this.state.activeFilter}`;
    if (this.state.cachedResults[cacheKey]) {
      this.renderResults(this.state.cachedResults[cacheKey]);
      return;
    }

    // Show loading state
    this.showLoadingState();

    // Create abort controller
    this.state.abortController = new AbortController();

    try {
      // Use correct Shopify Predictive Search API endpoint
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=${this.state.activeFilter === 'all' ? 'product,article,page' : this.state.activeFilter}&resources[limit]=${this.config.resources.limit}&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,variants.title,body`;

      const response = await fetch(searchUrl, {
        signal: this.state.abortController.signal,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();

      // Cache results
      this.state.cachedResults[cacheKey] = data;

      // Render results
      this.renderResults(data);

    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showEmptyState();
      }
    }
  }

  renderResults(data) {
    if (!data.resources?.results) {
      this.showEmptyState();
      return;
    }

    const hasResults = Object.values(data.resources.results).some(resource => resource.length > 0);

    if (!hasResults) {
      this.showEmptyState();
      return;
    }

    let html = '<div class="beezfy-search__results-wrapper">';

    // Products
    if (data.resources.results.products?.length > 0) {
      html += this.renderProductResults(data.resources.results.products);
    }

    // Articles
    if (data.resources.results.articles?.length > 0) {
      html += this.renderArticleResults(data.resources.results.articles);
    }

    // Pages
    if (data.resources.results.pages?.length > 0) {
      html += this.renderPageResults(data.resources.results.pages);
    }

    html += '</div>';

    // View all results link
    html += `
      <div class="beezfy-search__view-all">
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(this.state.searchTerm)}" class="beezfy-search__view-all-link">
          ${window.beezfyTranslations?.search?.view_all || 'View all results'} →
        </a>
      </div>
    `;

    this.elements.searchResults.innerHTML = html;
    this.showSearchState();
  }

  renderProductResults(products) {
    let html = `
      <div class="beezfy-search__result-group">
        <h3 class="beezfy-search__result-group-title">${window.beezfyTranslations?.search?.products || 'Products'}</h3>
        <div class="beezfy-search__products">
    `;

    products.forEach(product => {
      const priceValue = product.price || product.price_min || product.variants?.[0]?.price || '0';
      const comparePriceValue = product.compare_at_price_max || product.compare_at_price || product.variants?.[0]?.compare_at_price;

      const price = this.formatMoney(priceValue);
      const comparePrice = (comparePriceValue && parseFloat(comparePriceValue) > 0 && comparePriceValue !== priceValue)
        ? this.formatMoney(comparePriceValue)
        : null;
      let imageUrl = '';
      if (product.image) {
        imageUrl = product.image.includes('&') || product.image.includes('?')
          ? `${product.image}&width=200`
          : `${product.image}?width=200`;
      } else if (product.featured_image) {
        imageUrl = product.featured_image.includes('&') || product.featured_image.includes('?')
          ? `${product.featured_image}&width=200`
          : `${product.featured_image}?width=200`;
      }

      html += `
        <a href="${product.url}" class="beezfy-search__product-item beezfy-search__result-item" data-product-id="${product.id}">
          <div class="beezfy-search__product-image">
            ${imageUrl ? `
              <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" onerror="this.parentElement.classList.add('beezfy-search__product-image--placeholder')">
            ` : '<div class="beezfy-search__product-image--placeholder"></div>'}
          </div>
          <div class="beezfy-search__product-info">
            ${product.vendor ? `<span class="beezfy-search__product-vendor">${this.escapeHtml(product.vendor)}</span>` : ''}
            <h4 class="beezfy-search__product-title">${this.highlightMatch(product.title)}</h4>
            <div class="beezfy-search__product-price">
              <span class="beezfy-search__product-regular-price">${price}</span>
              ${comparePrice ? `<span class="beezfy-search__product-compare-price">${comparePrice}</span>` : ''}
            </div>
          </div>
        </a>
      `;
    });

    html += '</div></div>';
    return html;
  }

  renderArticleResults(articles) {
    let html = `
      <div class="beezfy-search__result-group">
        <h3 class="beezfy-search__result-group-title">${window.beezfyTranslations?.search?.articles || 'Articles'}</h3>
        <div class="beezfy-search__articles">
    `;

    articles.forEach(article => {
      html += `
        <a href="${article.url}" class="beezfy-search__article-item beezfy-search__result-item">
          <h4 class="beezfy-search__article-title">${this.highlightMatch(article.title)}</h4>
          ${article.summary ? `<p class="beezfy-search__article-summary">${article.summary}</p>` : ''}
        </a>
      `;
    });

    html += '</div></div>';
    return html;
  }

  renderPageResults(pages) {
    let html = `
      <div class="beezfy-search__result-group">
        <h3 class="beezfy-search__result-group-title">${window.beezfyTranslations?.search?.pages || 'Pages'}</h3>
        <div class="beezfy-search__pages">
    `;

    pages.forEach(page => {
      html += `
        <a href="${page.url}" class="beezfy-search__page-item beezfy-search__result-item">
          <h4 class="beezfy-search__page-title">${this.highlightMatch(page.title)}</h4>
          ${page.summary ? `<p class="beezfy-search__page-summary">${page.summary}</p>` : ''}
        </a>
      `;
    });

    html += '</div></div>';
    return html;
  }

  highlightMatch(text) {
    if (!this.state.searchTerm || !text) return text;

    const escapedTerm = this.state.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  escapeHtml(text) {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  formatMoney(price) {
    if (!price) return '€0,00';
    let amount = typeof price === 'string' ? parseFloat(price) : price;
    const money = amount.toFixed(2).replace('.', ',');
    return `€${money}`;
  }

  navigateResults(direction) {
    const results = Array.from(this.drawer.querySelectorAll('.beezfy-search__result-item'));
    if (!results.length) return;

    const currentIndex = results.findIndex(r => r.classList.contains('beezfy-search__result-item--active'));
    let newIndex;

    if (direction === 'down') {
      newIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, results.length - 1);
    } else {
      newIndex = currentIndex === -1 ? results.length - 1 : Math.max(currentIndex - 1, 0);
    }

    results.forEach((result, index) => {
      result.classList.toggle('beezfy-search__result-item--active', index === newIndex);
    });

    // Scroll into view
    if (results[newIndex]) {
      results[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  async loadTrendingProducts() {
    if (!this.elements.trendingProducts) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}collections/all/products.json?limit=4`);
      const data = await response.json();

      if (data.products?.length > 0) {
        let html = '<div class="beezfy-search__trending-grid">';

        data.products.forEach(product => {
          const variant = product.variants[0];
          const price = this.formatMoney(variant.price);
          const comparePrice = (variant.compare_at_price && variant.compare_at_price > 0 && variant.compare_at_price !== variant.price)
            ? this.formatMoney(variant.compare_at_price)
            : null;
          const discount = (variant.compare_at_price && variant.compare_at_price > variant.price)
            ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
            : 0;
          let imageUrl = '';
          if (product.images?.[0]?.src) {
            const src = product.images[0].src;
            imageUrl = src.includes('?') ? `${src}&width=400` : `${src}?width=400`;
          } else if (product.featured_image) {
            imageUrl = product.featured_image.includes('?')
              ? `${product.featured_image}&width=400`
              : `${product.featured_image}?width=400`;
          }

          html += `
            <a href="/products/${product.handle}" class="bfy-cart-item" data-item-id="${product.id}">
              <div class="bfy-cart-item__image">
                ${imageUrl ? `
                  <img src="${imageUrl}"
                       alt="${this.escapeHtml(product.title)}"
                       loading="lazy"
                       class="bfy-cart-item__img"
                       onerror="this.parentElement.innerHTML='<div class=\\'bfy-cart-item__placeholder\\'><svg width=\\'20\\' height=\\'20\\' viewBox=\\'0 0 24 24\\' fill=\\'none\\' stroke=\\'currentColor\\' stroke-width=\\'1\\'><rect x=\\'3\\' y=\\'3\\' width=\\'18\\' height=\\'18\\' rx=\\'2\\'/><circle cx=\\'8.5\\' cy=\\'8.5\\' r=\\'1.5\\'/><path d=\\'M21 15L16 10L5 21\\'/></svg></div>'">
                ` : `
                  <div class="bfy-cart-item__placeholder">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <path d="M21 15L16 10L5 21"/>
                    </svg>
                  </div>
                `}
              </div>

              <div class="bfy-cart-item__info">
                <div class="bfy-cart-item__title">
                  ${this.escapeHtml(product.title).substring(0, 30)}
                </div>


                <div class="bfy-cart-item__price-row">
                  <div class="bfy-cart-item__prices">
                    ${comparePrice ? `
                      <span class="bfy-cart-item__price bfy-cart-item__price--sale">
                        ${price}
                      </span>
                      ${discount > 0 ? `
                        <span class="bfy-cart-item__badge">-${discount}%</span>
                      ` : ''}
                    ` : `
                      <span class="bfy-cart-item__price">
                        ${price}
                      </span>
                    `}
                  </div>
                </div>
              </div>
            </a>
          `;
        });

        html += '</div>';
        this.elements.trendingProducts.innerHTML = html;
      }
    } catch (error) {
      this.elements.trendingProducts.innerHTML = `
        <div style="text-align: center; padding: 1rem; color: #666;">
          <p style="font-size: 13px;">Unable to load trending products</p>
        </div>
      `;
    }
  }

  showLoadingState() {
    this.elements.searchDefault.style.display = 'none';
    this.elements.searchResults.style.display = 'none';
    this.elements.searchEmpty.style.display = 'none';
    this.elements.searchLoading.style.display = 'flex';
  }

  showDefaultState() {
    this.elements.searchLoading.style.display = 'none';
    this.elements.searchResults.style.display = 'none';
    this.elements.searchEmpty.style.display = 'none';
    this.elements.searchDefault.style.display = 'block';
  }

  showSearchState() {
    this.elements.searchLoading.style.display = 'none';
    this.elements.searchDefault.style.display = 'none';
    this.elements.searchEmpty.style.display = 'none';
    this.elements.searchResults.style.display = 'block';
  }

  showEmptyState() {
    this.elements.searchLoading.style.display = 'none';
    this.elements.searchDefault.style.display = 'none';
    this.elements.searchResults.style.display = 'none';
    this.elements.searchEmpty.style.display = 'block';
  }

  onOpen() {
    this.state.isOpen = true;
    if (this.elements.searchInput) {
      setTimeout(() => this.elements.searchInput.focus(), 100);
    }
  }

  onClose() {
    this.state.isOpen = false;
    this.clearSearch();
  }

  clearSearch() {
    this.elements.searchInput.value = '';
    if (this.elements.searchClear) {
      this.elements.searchClear.classList.remove('show');
      setTimeout(() => {
        if (this.elements.searchClear) {
          this.elements.searchClear.style.display = 'none';
        }
      }, 200);
    }
    this.state.searchTerm = '';
    this.showDefaultState();
  }

  closeDrawer() {
    if (window.BeezfyDrawerSystem) {
      window.BeezfyDrawerSystem.close('search');
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.beezfySearchPremium = new BeezfySearchPremium();
  });
} else {
  window.beezfySearchPremium = new BeezfySearchPremium();
}